import { addPlanRecipeRequestSchema, cookedPreviewResponseSchema, createLeftoversRequestSchema, createMealPlanRequestSchema, markCookedRequestSchema, mealPlanDtoSchema, planListQuerySchema, planListResponseSchema, putPlanAttendeesRequestSchema, suggestionsQuerySchema, suggestionsResponseSchema, timelineQuerySchema, timelineResponseSchema, updateMealPlanRequestSchema, updatePlanRecipeRequestSchema, } from '@fewerbags/schema';
const plan = { parse: mealPlanDtoSchema };
/**
 * The sub-resource mutations (add/scale/remove a recipe, mark cooked) return the
 * refreshed plan where the API has one to give and `{ ok: true }` otherwise, so
 * they resolve to null rather than throwing. Callers invalidate the plan queries
 * either way.
 */
const maybePlan = {
    parse: (value) => {
        const parsed = mealPlanDtoSchema.safeParse(value);
        return parsed.success ? parsed.data : null;
    },
};
/**
 * /api/v1/plans — meal plans, the merged cooking timeline (PLAN §4.1),
 * mark-cooked pantry decrement (D10) and suggestions (§4.2).
 * Household-scoped: the household always comes from the session, never the client.
 */
export function createPlansApi(http, base) {
    const id = (planId) => `${base}/${encodeURIComponent(planId)}`;
    return {
        /** Plans between two inclusive YYYY-MM-DD dates (max 62 days). */
        list: async (from, to) => {
            const query = planListQuerySchema.parse({ from, to });
            return http.get(base, { query, parse: planListResponseSchema });
        },
        get: async (planId) => http.get(id(planId), plan),
        create: async (input) => http.post(base, createMealPlanRequestSchema.parse(input), plan),
        /**
         * Partial update of date / slot / serveAt / servings / status. Changing servings
         * rescales the plan's recipes; status 'cooked' goes through `markCooked`.
         */
        update: async (planId, input) => http.patch(id(planId), updateMealPlanRequestSchema.parse(input), plan),
        remove: async (planId) => http.del(id(planId)),
        /** Put a recipe on the plan; omit `scale` to let the API derive it from servings. */
        addRecipe: async (planId, input) => http.post(`${id(planId)}/recipes`, addPlanRecipeRequestSchema.parse(input), maybePlan),
        /** Re-scale one recipe on the plan. */
        updateRecipe: async (planId, recipeId, input) => http.patch(`${id(planId)}/recipes/${encodeURIComponent(recipeId)}`, updatePlanRecipeRequestSchema.parse(input), maybePlan),
        removeRecipe: async (planId, recipeId) => http.del(`${id(planId)}/recipes/${encodeURIComponent(recipeId)}`, undefined, maybePlan),
        /**
         * The merged, colour-coded timeline. `serveAt` overrides the plan's serve time
         * and `now` re-anchors it ("I'm running late") — both full ISO datetimes.
         */
        timeline: async (planId, input = {}) => {
            const query = timelineQuerySchema.parse(input);
            return http.get(`${id(planId)}/timeline`, { query, parse: timelineResponseSchema });
        },
        /** What marking this plan cooked would consume from the pantry, before confirming (D10). */
        cookedPreview: async (planId) => http.get(`${id(planId)}/cooked-preview`, { parse: cookedPreviewResponseSchema }),
        /** Confirm the decrement and set the plan to cooked; an empty list skips the pantry. */
        markCooked: async (planId, input = {}) => http.post(`${id(planId)}/cooked`, markCookedRequestSchema.parse(input), maybePlan),
        /** Replace who eats a plan (D32); empty = everyone. Servings follow by default. */
        setAttendees: async (planId, input = {}) => http.put(`${id(planId)}/attendees`, putPlanAttendeesRequestSchema.parse(input), plan),
        /** Save the extra servings of a plan as a linked leftovers meal on another day/slot. */
        createLeftovers: async (planId, input) => http.post(`${id(planId)}/leftovers`, createLeftoversRequestSchema.parse(input), plan),
        /** Recipe suggestions for a date/slot, each with human-readable reasons (§4.2). */
        suggestions: async (input) => {
            const q = suggestionsQuerySchema.parse(input);
            return http.get(`${base}/suggestions`, {
                query: {
                    date: q.date,
                    slot: q.slot,
                    limit: q.limit,
                    windowDays: q.windowDays,
                    includeLibrary: q.includeLibrary,
                },
                parse: suggestionsResponseSchema,
            });
        },
    };
}
//# sourceMappingURL=plans.js.map